import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  useGetAgentById,
  useGetAgents,
  usePublishAgent,
  useUpdateAgentAIConfigurations,
  useUpdateWidget,
} from 'features/chat/hooks/use-agents';
import {
  useGetCustomLlmModels,
  useGetLlmModels,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import {
  getAgentToolIds,
  WidgetType,
  type Agent,
  type CustomRule,
  type GuardrailConfig,
  type GuardrailOutputStrategy,
  type IPublishAgentResponse,
  type IUpdateAgentAIConfigurationPayload,
  type IUpdateAgentWidgetPayload,
  type Widget,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { RESOURCE_VISIBILITY, normalizeResourceVisibility } from 'types/visibility';
import { AgentConfigurationCard } from './components/agent-configuration-card';
import { isConfigSectionComingSoon } from './components/agent-configuration.constants';
import { EditAgent } from './components/edit-agent';
import { AgentsConfigPreviewSection } from './components/preview-section/agents-config-preview-section';
import { AgentsRetrievalTesting } from 'features/chat/agent-chat-module/components/agents-retrieval-testing';
import { AgentIntegrationHub } from './components/integrations';
import { AgentConversation } from 'features/chat/agent-chat-module/components/agent-conversation/agent-conversation';
import { HttpError } from 'lib/https';
import { AgentDetailsTopBar } from './components/agent-details-top-bar';
import {
  parseAllowedDomains,
  parseChatbotWidgetData,
} from './components/integrations/agent-integrations-chatbot/utils';
import {
  canConfigureAgentWidget,
  canEditAgentConfig,
  canManageKb,
  canManageSkills,
  canManageSubagents,
  canManageTools,
  hasAgentShareAccess,
  isAgentOwnerAccess,
} from './utils/agent-share-access';
import {
  AgentCapabilitiesState,
  BASE_PROMPT_SYNC_NOTICE,
  computeDefaultBasePrompt,
  isManagedDefaultPrompt,
  resolveSyncedBasePrompt,
} from 'utils/agent-default-prompt';

const capabilitiesFromSectionState = (state: Record<string, boolean>): AgentCapabilitiesState => ({
  enableTools: !!state.tools,
  enableSkills: !!state.skills,
  enableSubagents: !!state.subagents,
  enableKnowledgeBase: !!state['knowledge-base'],
});

/** Plain readable copy when UILM keys are missing or still untranslated. */
const agentText = (
  t: TFunction,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, { defaultValue: fallback, ...options });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(String(value))) {
    return fallback;
  }
  return String(value);
};

/** The backend seeds the greeting with a `[NAME]` placeholder; show the agent's name instead. */
const formatGreeting = (greeting: string, agentName: string) =>
  greeting.replace(/\[NAME\]/g, agentName);

const buildPublishWidgetPayload = (agent: Agent, formWidget: Widget): Widget => {
  const parsed = parseChatbotWidgetData(agent.widget);
  const restSettings = (agent.widget?.widget_settings ?? []).filter(
    (item) => item.widget_type !== WidgetType.CHAT
  );
  const chatFeStyle =
    agent.widget?.widget_settings?.find((item) => item.widget_type === WidgetType.CHAT)?.fe_style ??
    JSON.stringify(parsed.designSettings);

  return {
    ...(agent.widget ?? {}),
    ...formWidget,
    name: agent.name,
    logo_url: agent.widget?.logo_url ?? '',
    logo_id: agent.widget?.logo_id ?? null,
    greeting: formatGreeting(
      formWidget.greeting || agent.widget?.greeting || parsed.greeting,
      agent.name
    ),
    site_url: agent.widget?.site_url ?? null,
    allowed_domains: formWidget.allowed_domains ?? agent.widget?.allowed_domains ?? [],
    allow_visitor_tool_connect:
      agent.widget?.allow_visitor_tool_connect ?? agent.widget?.AllowVisitorToolConnect ?? true,
    host_cookie_name: agent.widget?.host_cookie_name ?? agent.widget?.HostCookieName ?? null,
    brand_color: agent.widget?.brand_color ?? null,
    fe_script: agent.widget?.fe_script ?? null,
    show_agent_name: agent.widget?.show_agent_name ?? false,
    predefined_questions: (
      formWidget.predefined_questions ??
      agent.widget?.predefined_questions ??
      []
    ).filter(Boolean),
    enable_predefined_questions:
      formWidget.enable_predefined_questions ?? agent.widget?.enable_predefined_questions ?? false,
    widget_settings: [
      ...restSettings,
      {
        widget_type: WidgetType.CHAT,
        fe_style: chatFeStyle,
      },
    ],
  };
};

const getAgentDetailsTabs = (agentId: string, t: TFunction) => [
  { label: agentText(t, 'EDIT_AGENT', 'Edit Agent'), href: `/agent-details/${agentId}/edit` },
  {
    label: agentText(t, 'CONFIGURATION', 'Configuration'),
    href: `/agent-details/${agentId}/configuration`,
  },
  {
    label: agentText(t, 'CHATBOT_INTGERATION', 'Integration'),
    href: `/agent-details/${agentId}/integrations`,
  },
  {
    label: agentText(t, 'CONVERSATIONS', 'Conversations'),
    href: `/agent-details/${agentId}/conversations`,
  },
];

export default function AgentDetailsPage() {
  const { t } = useTranslation();
  const { agentId = '' } = useParams();
  const { pathname, search, state: locationState } = useLocation();
  const navigate = useNavigate();

  // Redirect base route to /edit
  useEffect(() => {
    if (pathname === `/agent-details/${agentId}`) {
      navigate(`/agent-details/${agentId}/edit`, { replace: true });
    }
  }, [pathname, agentId, navigate]);
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const blocksKey = configurationSettings?.blocksKey || '';
  const defaultJsonResponseFormat = useMemo(
    () =>
      JSON.stringify(
        {
          answer: 'string',
          question: 'string',
        },
        null,
        2
      ),
    []
  );
  const [sectionState, setSectionState] = useState<Record<string, boolean>>({
    'knowledge-base': true,
    tools: false,
    skills: false,
    subagents: false,
    memory: false,
    'human-handoff': false,
    guardrails: true,
  });
  const defaultWelcomeMessage = agentText(
    t,
    'DEFAULT_WELCOME_MESSAGE_FINANCE_ASSISTANT',
    "Welcome! My name is Finance Assistant. If you need any assistance or have any questions, I'm here to help."
  );
  const [selectedLlmModel, setSelectedLlmModel] = useState('');
  const [selectedLlmProvider, setSelectedLlmProvider] = useState('');
  const [selectedLlmModelId, setSelectedLlmModelId] = useState('');
  const [isBlocksProvider, setIsBlocksProvider] = useState(true);
  const [responseType, setResponseType] = useState('text');
  const [responseFormat, setResponseFormat] = useState<string | null>(null);
  const [basePrompt, setBasePrompt] = useState('');
  const [temperatureValue, setTemperatureValue] = useState(0.7);
  const [maxResponseValue, setMaxResponseValue] = useState(10000);
  const [isModelParametersOpen, setIsModelParametersOpen] = useState(false);
  const [enableQueryEnhancement, setEnableQueryEnhancement] = useState(true);
  const [scoreThreshold, setScoreThreshold] = useState(0.7);
  const [topK, setTopK] = useState(10);
  const [denseWeight, setDenseWeight] = useState(0.9);
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [selectedSubagentIds, setSelectedSubagentIds] = useState<string[]>([]);
  const [enableSummary, setEnableSummary] = useState(true);
  const [lastNConversations, setLastNConversations] = useState(5);
  const [greeting, setGreeting] = useState('');
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  const [enableSmartSuggestions, setEnableSmartSuggestions] = useState(true);
  const [enablePresetQuestions, setEnablePresetQuestions] = useState(true);
  const [presetQuestions, setPresetQuestions] = useState<string[]>([]);
  const [guardrailOutputStrategy, setGuardrailOutputStrategy] =
    useState<GuardrailOutputStrategy>('block');
  const [configuredGuardrails, setConfiguredGuardrails] = useState<Record<string, GuardrailConfig>>(
    {}
  );
  const [guardrailErrors, setGuardrailErrors] = useState<Record<string, string>>({});
  // Previous safety mechanism state preserved:
  const [enablePreValidation, setEnablePreValidation] = useState(true);
  const [enablePostValidation, setEnablePostValidation] = useState(true);
  const [enablePiiDetection, setEnablePiiDetection] = useState(false);
  const [enableInjectionDetection, setEnableInjectionDetection] = useState(false);
  const [inputRiskThreshold, setInputRiskThreshold] = useState(0.7);
  const [outputRiskThreshold, setOutputRiskThreshold] = useState(0.8);
  const [enableKeywordCheck, setEnableKeywordCheck] = useState(false);
  const [bannedKeywords, setBannedKeywords] = useState<string[]>([]);
  const [bannedPatterns, setBannedPatterns] = useState<string[]>([]);
  const [enableCustomRules, setEnableCustomRules] = useState(false);
  const [customRules, setCustomRules] = useState<CustomRule[]>([]);
  const isHydratingRef = useRef(true);
  const hydratedForAgentIdRef = useRef<string | null>(null);
  const prevCapabilitiesRef = useRef<AgentCapabilitiesState | null>(null);
  const agentRef = useRef<Agent | undefined>(undefined);
  const lastSavedSignatureRef = useRef('');
  const currentSignatureRef = useRef('');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const editPersonaSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  const {
    data,
    isLoading: isLoadingAgent,
    refetch: refetchAgent,
  } = useGetAgentById(
    { id: agentId, projectKey: blocksKey },
    { enabled: !!blocksKey && !!agentId, staleTime: 0, refetchOnMount: 'always' }
  );
  const { data: llmModels, isLoading: isLoadingLlmModels } = useGetLlmModels();
  const { data: customLlmModels, isLoading: isLoadingCustomLlmModels } = useGetCustomLlmModels();
  const { mutateAsync: updateAgentAIConfigurations } = useUpdateAgentAIConfigurations();
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const { mutateAsync: publishAgent, isPending: isPublishing } = usePublishAgent();
  const [isPublishFlow, setIsPublishFlow] = useState(false);
  const agent = data?.agent;
  const userId = useCommonStore((state) => state.user?.id);
  const isMyOrganization =
    Boolean(agent) && normalizeResourceVisibility(agent?.visibility) === RESOURCE_VISIBILITY.PUBLIC;
  const isOwner = isAgentOwnerAccess(agent, userId);
  const canEdit = canEditAgentConfig(agent, userId);
  // The widget is saved under the server's own rule (creator in the agent's own org,
  // org admin or an explicit editor for one shared in), not under agent ownership.
  const canConfigureWidget = canConfigureAgentWidget(agent, userId);
  const isSharedWithMe = hasAgentShareAccess(agent);
  const manageTools = canManageTools(agent, userId);
  const manageSkills = canManageSkills(agent, userId);
  const manageSubagents = canManageSubagents(agent, userId);
  const manageKb = canManageKb(agent, userId);
  agentRef.current = agent;
  const agentName = agent?.name || agentText(t, 'FINANCE_ASSISTANT', 'Finance Assistant');
  const isEmptyWidgetId = Boolean(agent) && !agent?.widget_id;
  const isPublishBusy = isPublishing || isPublishFlow;
  const previewMessage = greeting || agent?.widget?.greeting || defaultWelcomeMessage;
  const tabItems = getAgentDetailsTabs(agentId, t);
  const { data: agentsListData } = useGetAgents(
    { limit: 100, offset: 0, project_key: blocksKey },
    { enabled: !!blocksKey, scope: 'agent-details-switcher' }
  );
  const availableAgents = agentsListData?.agents ?? [];
  const availableChatModels = useMemo(
    () => (llmModels || []).filter((model) => model.model_type === 'chat'),
    [llmModels]
  );
  const availableCustomChatModels = useMemo(
    () =>
      (customLlmModels?.models || []).filter(
        (model) =>
          model.ModelType === 'chat' &&
          model.IsActive === true &&
          model.Status.toLowerCase() === 'valid'
      ),
    [customLlmModels]
  );
  const currentLlmModel = selectedLlmModel || agent?.llm_config?.model_name || '';
  const currentLlmProvider = selectedLlmProvider || agent?.llm_config?.provider || '';
  const currentLlmModelId = selectedLlmModelId || agent?.llm_config?.model_id || '';
  const currentIsBlocksProvider = selectedLlmProvider
    ? isBlocksProvider
    : (agent?.llm_config?.is_blocks_provider ?? true);

  const widgetPayload = useMemo(
    (): Widget => ({
      ...(agent?.widget ?? {}),
      // A custom widget name set on the Integration tab wins; otherwise use the agent's name.
      name: agent?.widget?.name || agent?.name || '',
      widget_settings: agent?.widget?.widget_settings ?? [],
      logo_url: agent?.widget?.logo_url ?? '',
      logo_id: agent?.widget?.logo_id ?? null,
      fe_script: agent?.widget?.fe_script ?? null,
      show_agent_name: agent?.widget?.show_agent_name ?? true,
      site_url: agent?.widget?.site_url ?? null,
      brand_color: agent?.widget?.brand_color ?? null,
      allowed_domains: parseAllowedDomains(allowedDomainsInput),
      allow_visitor_tool_connect:
        agent?.widget?.allow_visitor_tool_connect ?? agent?.widget?.AllowVisitorToolConnect ?? true,
      greeting,
      predefined_questions: presetQuestions.map((question) => question.trim()).filter(Boolean),
      enable_predefined_questions: enablePresetQuestions,
      enable_questions_suggestions: enableSmartSuggestions,
    }),
    [
      agent?.name,
      agent?.widget,
      allowedDomainsInput,
      enablePresetQuestions,
      enableSmartSuggestions,
      greeting,
      presetQuestions,
    ]
  );

  const buildConfigPayload = useCallback((): IUpdateAgentAIConfigurationPayload | null => {
    const agentSnapshot = agentRef.current;
    if (!agentSnapshot) return null;

    const payload: IUpdateAgentAIConfigurationPayload = {
      agent_id: agentId,
      project_key: blocksKey,
      name: agentSnapshot.name,
      description: agentSnapshot.description,
      enable_guardrails: !!sectionState.guardrails,
      enable_memory: !!sectionState.memory,
      enable_human_handoff: isConfigSectionComingSoon('human-handoff')
        ? false
        : !!sectionState['human-handoff'],
      response_type: responseType,
      response_format: responseFormat,
      llm_config: {
        ...agentSnapshot.llm_config,
        provider: currentLlmProvider || agentSnapshot.llm_config.provider,
        model_name: currentLlmModel || agentSnapshot.llm_config.model_name,
        is_blocks_provider: currentIsBlocksProvider,
        model_id: currentLlmModelId || '',
        base_prompt: basePrompt,
        temperature: temperatureValue,
        max_tokens: maxResponseValue,
      },
      memory_config: {
        ...agentSnapshot.memory_config,
        enable_summary: enableSummary,
        last_n_conversations: lastNConversations,
        enable_questions_suggestions: enableSmartSuggestions,
      },
      rag_config: {
        ...agentSnapshot.rag_config,
        enable_query_enhancement: enableQueryEnhancement,
        score_threshold: scoreThreshold,
        top_k: topK,
        dense_weight: denseWeight,
        keyword_weight: Number((1 - denseWeight).toFixed(2)),
      },
      guardrail_output_strategy: guardrailOutputStrategy,
      guardrails: Object.entries(configuredGuardrails).reduce(
        (acc, [key, val]) => {
          const cleaned: GuardrailConfig = {
            enabled: val?.enabled ?? true,
          };
          if (Array.isArray(val?.keywords)) {
            cleaned.keywords = val.keywords.map((kw: string) => kw.trim()).filter(Boolean);
          }
          acc[key] = cleaned;
          return acc;
        },
        {} as Record<string, GuardrailConfig>
      ),
      /* Previous safety mechanism payload preserved:
      guardrail: {
        ...agentSnapshot.guardrail,
        enable_output_safety: agentSnapshot.guardrail?.enable_output_safety ?? false,
        enable_pre_validation: enablePreValidation,
        enable_post_validation: enablePostValidation,
        enable_pii_detection: enablePiiDetection,
        enable_injection_detection: enableInjectionDetection,
        enable_keyword_check: enableKeywordCheck,
        enable_custom_rules: enableCustomRules,
        banned_keywords: bannedKeywords,
        banned_patterns: bannedPatterns,
        input_risk_threshold: inputRiskThreshold,
        output_risk_threshold: outputRiskThreshold,
        custom_rules: customRules.map((rule) => ({
          ...rule,
          message: rule.message ?? '',
        })),
      },
      */
    };

    // Only send manage* fields the caller may change — BE returns 403 otherwise.
    if (manageKb) {
      payload.enable_rag = !!sectionState['knowledge-base'];
      payload.enable_knowledge_base = !!sectionState['knowledge-base'];
      payload.kb_ids = agentSnapshot.kb_ids ?? [];
      payload.folder_ids = selectedFolderIds;
    }
    if (manageTools) {
      payload.enable_tools = !!sectionState.tools;
      payload.tool_ids = selectedToolIds;
    }
    if (manageSkills) {
      payload.enable_skills = !!sectionState.skills;
      payload.skill_ids = selectedSkillIds;
    }
    if (manageSubagents) {
      payload.enable_subagents = !!sectionState.subagents;
      payload.subagent_ids = selectedSubagentIds;
    }

    return payload;
  }, [
    agentId,
    blocksKey,
    sectionState,
    responseType,
    responseFormat,
    currentLlmModel,
    currentLlmProvider,
    currentLlmModelId,
    currentIsBlocksProvider,
    basePrompt,
    temperatureValue,
    maxResponseValue,
    enableSummary,
    lastNConversations,
    enableSmartSuggestions,
    enableQueryEnhancement,
    scoreThreshold,
    topK,
    denseWeight,
    guardrailOutputStrategy,
    configuredGuardrails,
    selectedFolderIds,
    selectedToolIds,
    selectedSkillIds,
    selectedSubagentIds,
    manageKb,
    manageTools,
    manageSkills,
    manageSubagents,
  ]);

  const configSignature = useMemo(
    () =>
      JSON.stringify({
        sectionState,
        currentLlmModel,
        currentLlmProvider,
        currentLlmModelId,
        currentIsBlocksProvider,
        responseType,
        responseFormat,
        basePrompt,
        temperatureValue,
        maxResponseValue,
        enableQueryEnhancement,
        scoreThreshold,
        topK,
        denseWeight,
        selectedFolderIds,
        selectedToolIds,
        selectedSkillIds,
        selectedSubagentIds,
        enableSummary,
        lastNConversations,
        greeting,
        enableSmartSuggestions,
        enablePresetQuestions,
        presetQuestions,
        guardrailOutputStrategy,
        configuredGuardrails,
        enablePreValidation,
        enablePostValidation,
        enablePiiDetection,
        enableInjectionDetection,
        inputRiskThreshold,
        outputRiskThreshold,
        enableKeywordCheck,
        bannedKeywords,
        bannedPatterns,
        enableCustomRules,
        customRules,
      }),
    [
      sectionState,
      currentLlmModel,
      currentLlmProvider,
      currentLlmModelId,
      currentIsBlocksProvider,
      responseType,
      responseFormat,
      basePrompt,
      temperatureValue,
      maxResponseValue,
      enableQueryEnhancement,
      scoreThreshold,
      topK,
      denseWeight,
      selectedFolderIds,
      selectedToolIds,
      selectedSkillIds,
      selectedSubagentIds,
      enableSummary,
      lastNConversations,
      greeting,
      enableSmartSuggestions,
      enablePresetQuestions,
      presetQuestions,
      guardrailOutputStrategy,
      configuredGuardrails,
      enablePreValidation,
      enablePostValidation,
      enablePiiDetection,
      enableInjectionDetection,
      inputRiskThreshold,
      outputRiskThreshold,
      enableKeywordCheck,
      bannedKeywords,
      bannedPatterns,
      enableCustomRules,
      customRules,
    ]
  );

  useEffect(() => {
    currentSignatureRef.current = configSignature;
  }, [configSignature]);

  const isMountedRef = useRef(false);
  const prevPathnameRef = useRef(pathname);

  // Refetch agent when transitioning to /configuration route (avoiding duplicate calls on initial mount)
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      prevPathnameRef.current = pathname;
      return;
    }

    const isConfigurationRoute = pathname.includes('/configuration');
    const hasPathnameChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;

    if (hasPathnameChanged && isConfigurationRoute && agentId && blocksKey) {
      refetchAgent();
    }
  }, [pathname, agentId, blocksKey, refetchAgent]);

  // Reset hydration flags on route changes
  useEffect(() => {
    isHydratingRef.current = true;
    hydratedForAgentIdRef.current = null;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  }, [pathname, agentId]);

  useEffect(() => {
    if (!agent || agent.id !== agentId) return;
    const pageAgentKey = `${agentId}_${pathname}`;
    if (hydratedForAgentIdRef.current === pageAgentKey) return;
    hydratedForAgentIdRef.current = pageAgentKey;

    const incomingFolderId =
      (typeof locationState === 'object' && locationState && 'addFolderId' in locationState
        ? (locationState as { addFolderId?: string }).addFolderId
        : undefined) || new URLSearchParams(search).get('addFolder');
    const incomingToolId =
      (typeof locationState === 'object' && locationState && 'addToolId' in locationState
        ? (locationState as { addToolId?: string }).addToolId
        : undefined) || new URLSearchParams(search).get('addTool');
    const incomingSkillId =
      (typeof locationState === 'object' && locationState && 'addSkillId' in locationState
        ? (locationState as { addSkillId?: string }).addSkillId
        : undefined) || new URLSearchParams(search).get('addSkill');
    const incomingSubagentId =
      (typeof locationState === 'object' && locationState && 'addSubagentId' in locationState
        ? (locationState as { addSubagentId?: string }).addSubagentId
        : undefined) || new URLSearchParams(search).get('addSubagent');
    const originalKbEnabled = agent.enable_knowledge_base ?? agent.enable_rag ?? true;
    const originalToolsEnabled = agent.enable_tools ?? false;
    const originalSkillsEnabled = agent.enable_skills ?? false;
    const originalSubagentsEnabled = agent.enable_subagents ?? false;
    const agentFolderIds = agent.folder_ids ?? [];
    const agentToolIds = getAgentToolIds(agent);
    const agentSkillIds = agent.skill_ids ?? [];
    const agentSubagentIds = agent.subagent_ids ?? [];
    const nextFolderIds =
      incomingFolderId && !agentFolderIds.includes(incomingFolderId)
        ? [...agentFolderIds, incomingFolderId]
        : agentFolderIds;
    const nextToolIds =
      incomingToolId && !agentToolIds.includes(incomingToolId)
        ? [...agentToolIds, incomingToolId]
        : agentToolIds;
    const nextSkillIds =
      incomingSkillId && !agentSkillIds.includes(incomingSkillId)
        ? [...agentSkillIds, incomingSkillId]
        : agentSkillIds;
    const nextSubagentIds =
      incomingSubagentId &&
      incomingSubagentId !== agentId &&
      !agentSubagentIds.includes(incomingSubagentId)
        ? [...agentSubagentIds, incomingSubagentId]
        : agentSubagentIds;

    const nextSectionState = {
      'knowledge-base': incomingFolderId ? true : originalKbEnabled,
      tools: incomingToolId ? true : originalToolsEnabled,
      skills: incomingSkillId ? true : originalSkillsEnabled,
      subagents: incomingSubagentId ? true : originalSubagentsEnabled,
      memory: !!agent.enable_memory,
      'human-handoff': isConfigSectionComingSoon('human-handoff')
        ? false
        : (agent.enable_human_handoff ?? false),
      guardrails:
        agent.enable_guardrails ??
        ((agent.guardrail?.enable_pre_validation ?? false) ||
          (agent.guardrail?.enable_post_validation ?? false)),
    };
    setSectionState(nextSectionState);
    const initialCapabilities = capabilitiesFromSectionState(nextSectionState);
    prevCapabilitiesRef.current = initialCapabilities;

    if (agent.llm_config?.model_name) {
      setSelectedLlmModel(agent.llm_config.model_name);
    }
    setSelectedLlmProvider(agent.llm_config?.provider || '');
    setSelectedLlmModelId(agent.llm_config?.model_id || '');
    setIsBlocksProvider(agent.llm_config?.is_blocks_provider ?? true);
    setResponseType(agent.response_type || 'text');
    setResponseFormat(agent.response_format ?? null);
    // Empty or still-managed-default prompts auto-fill from current capabilities;
    // a custom, user-authored prompt is left untouched.
    const storedBasePrompt = agent.llm_config?.base_prompt ?? '';
    const initialBasePrompt =
      !storedBasePrompt.trim() || isManagedDefaultPrompt(storedBasePrompt)
        ? computeDefaultBasePrompt(initialCapabilities)
        : storedBasePrompt;
    setBasePrompt(initialBasePrompt);
    setTemperatureValue(Math.min(agent.llm_config?.temperature ?? 0.7, 1));
    setMaxResponseValue(Math.min(agent.llm_config?.max_tokens ?? 10000, 10000));

    setEnableQueryEnhancement(agent.rag_config?.enable_query_enhancement ?? true);
    setScoreThreshold(agent.rag_config?.score_threshold ?? 0.7);
    setTopK(agent.rag_config?.top_k ?? 10);
    setDenseWeight(agent.rag_config?.dense_weight ?? 0.9);
    setSelectedFolderIds(nextFolderIds);
    setSelectedToolIds(nextToolIds);
    setSelectedSkillIds(nextSkillIds);
    setSelectedSubagentIds(nextSubagentIds);

    setEnableSummary(agent.memory_config?.enable_summary ?? true);
    setLastNConversations(agent.memory_config?.last_n_conversations ?? 5);
    const initialGreeting = formatGreeting(agent.widget?.greeting ?? '', agent.name);
    setGreeting(initialGreeting);
    setAllowedDomainsInput((agent.widget?.allowed_domains ?? []).filter(Boolean).join(', '));
    setEnableSmartSuggestions(
      agent.memory_config?.enable_questions_suggestions ??
        agent.widget?.enable_questions_suggestions ??
        true
    );
    setEnablePresetQuestions(agent.widget?.enable_predefined_questions ?? true);
    setPresetQuestions(agent.widget?.predefined_questions ?? []);

    const initialGuardrailOutputStrategy = agent.guardrail_output_strategy ?? 'block';
    const initialConfiguredGuardrails = agent.guardrails ?? {};
    setGuardrailOutputStrategy(initialGuardrailOutputStrategy);
    setConfiguredGuardrails(initialConfiguredGuardrails);

    setEnablePreValidation(agent.guardrail?.enable_pre_validation ?? true);
    setEnablePostValidation(agent.guardrail?.enable_post_validation ?? true);
    setEnablePiiDetection(agent.guardrail?.enable_pii_detection ?? false);
    setEnableInjectionDetection(agent.guardrail?.enable_injection_detection ?? false);
    setInputRiskThreshold(agent.guardrail?.input_risk_threshold ?? 0.7);
    setOutputRiskThreshold(agent.guardrail?.output_risk_threshold ?? 0.8);
    setEnableKeywordCheck(agent.guardrail?.enable_keyword_check ?? false);
    setBannedKeywords(agent.guardrail?.banned_keywords ?? []);
    setBannedPatterns(agent.guardrail?.banned_patterns ?? []);
    setEnableCustomRules(agent.guardrail?.enable_custom_rules ?? false);
    setCustomRules(agent.guardrail?.custom_rules ?? []);

    const savedSectionState = {
      ...nextSectionState,
      ...(incomingFolderId ? { 'knowledge-base': originalKbEnabled } : {}),
      ...(incomingToolId ? { tools: originalToolsEnabled } : {}),
      ...(incomingSkillId ? { skills: originalSkillsEnabled } : {}),
      ...(incomingSubagentId ? { subagents: originalSubagentsEnabled } : {}),
    };
    const initialSignature = JSON.stringify({
      sectionState: savedSectionState,
      currentLlmModel: agent.llm_config?.model_name || '',
      currentLlmProvider: agent.llm_config?.provider || '',
      currentLlmModelId: agent.llm_config?.model_id || '',
      currentIsBlocksProvider: agent.llm_config?.is_blocks_provider ?? true,
      responseType: agent.response_type || 'text',
      responseFormat: agent.response_format ?? null,
      basePrompt: initialBasePrompt,
      temperatureValue: Math.min(agent.llm_config?.temperature ?? 0.7, 1),
      maxResponseValue: Math.min(agent.llm_config?.max_tokens ?? 10000, 10000),
      enableQueryEnhancement: agent.rag_config?.enable_query_enhancement ?? true,
      scoreThreshold: agent.rag_config?.score_threshold ?? 0.7,
      topK: agent.rag_config?.top_k ?? 10,
      denseWeight: agent.rag_config?.dense_weight ?? 0.9,
      selectedFolderIds: agent.folder_ids ?? [],
      selectedToolIds: getAgentToolIds(agent),
      selectedSkillIds: agent.skill_ids ?? [],
      selectedSubagentIds: agent.subagent_ids ?? [],
      enableSummary: agent.memory_config?.enable_summary ?? true,
      lastNConversations: agent.memory_config?.last_n_conversations ?? 5,
      greeting: initialGreeting,
      enableSmartSuggestions:
        agent.memory_config?.enable_questions_suggestions ??
        agent.widget?.enable_questions_suggestions ??
        true,
      enablePresetQuestions: agent.widget?.enable_predefined_questions ?? true,
      presetQuestions: agent.widget?.predefined_questions ?? [],
      guardrailOutputStrategy: initialGuardrailOutputStrategy,
      configuredGuardrails: initialConfiguredGuardrails,
      enablePreValidation: agent.guardrail?.enable_pre_validation ?? true,
      enablePostValidation: agent.guardrail?.enable_post_validation ?? true,
      enablePiiDetection: agent.guardrail?.enable_pii_detection ?? false,
      enableInjectionDetection: agent.guardrail?.enable_injection_detection ?? false,
      inputRiskThreshold: agent.guardrail?.input_risk_threshold ?? 0.7,
      outputRiskThreshold: agent.guardrail?.output_risk_threshold ?? 0.8,
      enableKeywordCheck: agent.guardrail?.enable_keyword_check ?? false,
      bannedKeywords: agent.guardrail?.banned_keywords ?? [],
      bannedPatterns: agent.guardrail?.banned_patterns ?? [],
      enableCustomRules: agent.guardrail?.enable_custom_rules ?? false,
      customRules: agent.guardrail?.custom_rules ?? [],
    });

    lastSavedSignatureRef.current = initialSignature;
    isHydratingRef.current = false;

    if (
      (incomingFolderId || incomingToolId || incomingSkillId || incomingSubagentId) &&
      (search.includes('addFolder') ||
        search.includes('addTool') ||
        search.includes('addSkill') ||
        search.includes('addSubagent') ||
        (typeof locationState === 'object' &&
          locationState &&
          ('addFolderId' in locationState ||
            'addToolId' in locationState ||
            'addSkillId' in locationState ||
            'addSubagentId' in locationState)))
    ) {
      navigate(pathname, { replace: true, state: {} });
    }
  }, [agent, agentId, pathname, search, locationState, navigate]);

  const restoreLastSavedConfiguration = useCallback(() => {
    try {
      const saved = JSON.parse(lastSavedSignatureRef.current) as {
        selectedToolIds?: string[];
        selectedSkillIds?: string[];
        selectedSubagentIds?: string[];
        sectionState?: Record<string, boolean>;
      };
      if (saved.selectedToolIds) {
        setSelectedToolIds(saved.selectedToolIds);
      }
      if (saved.selectedSkillIds) {
        setSelectedSkillIds(saved.selectedSkillIds);
      }
      if (Array.isArray(saved.selectedSubagentIds)) {
        setSelectedSubagentIds(saved.selectedSubagentIds);
      }
      if (saved.sectionState) {
        setSectionState(saved.sectionState);
      }
    } catch {
      setSelectedToolIds(agentRef.current ? getAgentToolIds(agentRef.current) : []);
      setSelectedSkillIds(agentRef.current?.skill_ids ?? []);
      setSelectedSubagentIds(agentRef.current?.subagent_ids ?? []);
    }
  }, []);

  useEffect(() => {
    if (!canEdit || !agentRef.current || !blocksKey || isHydratingRef.current) {
      return;
    }

    if (lastSavedSignatureRef.current === configSignature) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const signatureToSave = configSignature;

    saveTimeoutRef.current = setTimeout(async () => {
      const widgetUpdatePayload: IUpdateAgentWidgetPayload = {
        agent_id: agentId,
        project_key: blocksKey,
        regen_widget_id: false,
        widget: widgetPayload,
      };
      const configPayload = buildConfigPayload();

      if (!configPayload) {
        return;
      }

      try {
        setIsSavingPreview(true);
        // Widget / embed is owner-only; editors save via update-ai-configurations.
        if (canConfigureWidget) {
          await updateWidget(widgetUpdatePayload);
        }
        await updateAgentAIConfigurations(configPayload);
        lastSavedSignatureRef.current = signatureToSave;
        setLastSaveTime(new Date());
      } catch (error) {
        restoreLastSavedConfiguration();

        const description =
          error instanceof HttpError && typeof error.error.detail === 'string'
            ? error.error.detail
            : agentText(
                t,
                'AGENT_CONFIGURATION_COULD_NOT_BE_SAVED',
                'Your changes could not be saved. Please try again.'
              );

        toast({
          variant: 'destructive',
          title: agentText(t, 'SAVE_FAILED', 'Save failed'),
          description,
        });
      } finally {
        setIsSavingPreview(false);
      }
    }, 700);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    agentId,
    blocksKey,
    canEdit,
    configSignature,
    isOwner,
    t,
    toast,
    restoreLastSavedConfiguration,
    updateAgentAIConfigurations,
    updateWidget,
    buildConfigPayload,
    widgetPayload,
  ]);

  const handleRegisterEditSave = useCallback((saveFn: (() => Promise<void>) | null) => {
    editPersonaSaveRef.current = saveFn;
  }, []);

  const autoBasePrompt = useMemo(
    () => computeDefaultBasePrompt(capabilitiesFromSectionState(sectionState)),
    [sectionState]
  );

  const handleSectionStateChange = useCallback(
    (key: string, checked: boolean) => {
      const next = { ...sectionState, [key]: checked };

      if (key === 'tools' || key === 'skills' || key === 'subagents' || key === 'knowledge-base') {
        const prevCapabilities =
          prevCapabilitiesRef.current ?? capabilitiesFromSectionState(sectionState);
        const nextCapabilities = capabilitiesFromSectionState(next);
        const resyncedPrompt = resolveSyncedBasePrompt({
          proposedBasePrompt: basePrompt,
          prevCapabilities,
          nextCapabilities,
        });
        prevCapabilitiesRef.current = nextCapabilities;

        if (resyncedPrompt !== basePrompt) {
          setBasePrompt(resyncedPrompt);
          toast({
            variant: 'info',
            title: agentText(t, 'BASE_PROMPT_UPDATED', BASE_PROMPT_SYNC_NOTICE.title),
            description: agentText(
              t,
              'BASE_PROMPT_UPDATED_DESCRIPTION',
              BASE_PROMPT_SYNC_NOTICE.description
            ),
          });
        }
      }

      setSectionState(next);
    },
    [sectionState, basePrompt, t, toast]
  );

  const handleSaveConfiguration = useCallback(async () => {
    if (!agentId || !blocksKey) {
      toast({
        title: agentText(t, 'SAVE_FAILED', 'Save failed'),
        description: agentText(t, 'MISSING_AGENT_OR_PROJECT_KEY', 'Missing agent or project key.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (editPersonaSaveRef.current) {
        await editPersonaSaveRef.current();
      }

      const configPayload = buildConfigPayload();
      if (!configPayload) {
        return;
      }

      setIsSavingPreview(true);
      if (canConfigureWidget) {
        await updateWidget({
          agent_id: agentId,
          project_key: blocksKey,
          regen_widget_id: false,
          widget: widgetPayload,
        });
      }
      await updateAgentAIConfigurations(configPayload);
      lastSavedSignatureRef.current = configSignature;
      setLastSaveTime(new Date());

      toast({
        variant: 'success',
        title: agentText(t, 'SAVED', 'Saved'),
        description: agentText(
          t,
          'AGENT_CONFIGURATION_SAVED',
          'Your changes were saved successfully.'
        ),
      });
    } catch (error) {
      restoreLastSavedConfiguration();

      const description =
        error instanceof HttpError && typeof error.error.detail === 'string'
          ? error.error.detail
          : agentText(
              t,
              'AGENT_CONFIGURATION_COULD_NOT_BE_SAVED',
              'Your changes could not be saved. Please try again.'
            );

      toast({
        variant: 'destructive',
        title: agentText(t, 'SAVE_FAILED', 'Save failed'),
        description,
      });
    } finally {
      setIsSavingPreview(false);
    }
  }, [
    agentId,
    blocksKey,
    buildConfigPayload,
    configSignature,
    isOwner,
    t,
    toast,
    restoreLastSavedConfiguration,
    updateAgentAIConfigurations,
    updateWidget,
    widgetPayload,
  ]);

  const handlePublish = useCallback(async () => {
    if (!isOwner) {
      await handleSaveConfiguration();
      return;
    }

    if (!agentId || !blocksKey) {
      toast({
        title: agentText(t, 'UNABLE_TO_PUBLISH', 'Unable to publish'),
        description: agentText(t, 'MISSING_AGENT_OR_PROJECT_KEY', 'Missing agent or project key.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsPublishFlow(true);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (editPersonaSaveRef.current) {
        await editPersonaSaveRef.current();
      }

      const configPayload = buildConfigPayload();
      if (configPayload) {
        // Widget edits are autosaved; publish only touches the widget to generate a
        // missing widget ID (older agents created without one).
        if (agent && !agent.widget_id) {
          await updateWidget({
            agent_id: agentId,
            project_key: blocksKey,
            regen_widget_id: true,
            widget: buildPublishWidgetPayload(agent, widgetPayload),
          });
        }
        await updateAgentAIConfigurations(configPayload);
        lastSavedSignatureRef.current = configSignature;
      }

      const response = (await publishAgent({
        agent_id: agentId,
        project_key: blocksKey,
      })) as IPublishAgentResponse;

      if (response.is_success) {
        toast({
          variant: 'success',
          title: agentText(t, 'AGENT_PUBLISHED', 'Agent published'),
          description: agentText(
            t,
            'AGENT_PUBLISHED_SUCCESSFULLY',
            'The agent was published successfully.'
          ),
        });
        return;
      }

      toast({
        title: agentText(t, 'PUBLISH_FAILED', 'Publish failed'),
        description:
          typeof response.detail === 'string'
            ? response.detail
            : agentText(t, 'AGENT_COULD_NOT_BE_PUBLISHED', 'The agent could not be published.'),
        variant: 'destructive',
      });
    } catch (error) {
      let description = agentText(
        t,
        'AGENT_COULD_NOT_BE_PUBLISHED',
        'The agent could not be published.'
      );

      if (error instanceof HttpError && typeof error.error?.detail === 'string') {
        description = error.error.detail;
      } else if (error instanceof Error && error.message) {
        try {
          const parsed = JSON.parse(error.message) as { detail?: unknown };
          description =
            typeof parsed.detail === 'string' && parsed.detail.trim()
              ? parsed.detail
              : error.message;
        } catch {
          description = error.message;
        }
      }

      toast({
        title: agentText(t, 'PUBLISH_FAILED', 'Publish failed'),
        description,
        variant: 'destructive',
      });
    } finally {
      setIsPublishFlow(false);
    }
  }, [
    agent,
    agentId,
    blocksKey,
    buildConfigPayload,
    configSignature,
    handleSaveConfiguration,
    isOwner,
    publishAgent,
    t,
    toast,
    updateAgentAIConfigurations,
    updateWidget,
    widgetPayload,
  ]);

  const handleTabClick = useCallback(
    (href: string) => {
      navigate(href);
    },
    [navigate]
  );

  const handleCancel = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  const handleAgentSelect = useCallback(
    (nextAgentId: string) => {
      if (nextAgentId === agentId) return;
      navigate(`/agent-details/${nextAgentId}/edit`);
    },
    [agentId, navigate]
  );

  const activeTabHref = useMemo(() => {
    const matchedTab = tabItems.find((tab) => pathname === tab.href);
    return matchedTab?.href ?? tabItems[0]?.href ?? '';
  }, [pathname, tabItems]);

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <AgentDetailsTopBar
        agentName={agentName}
        agents={availableAgents}
        tabs={tabItems}
        activeTabHref={activeTabHref}
        isLoading={isLoadingAgent}
        isPublishing={isPublishBusy}
        onBack={handleBack}
        onAgentSelect={handleAgentSelect}
        onTabClick={handleTabClick}
        onCancel={handleCancel}
        onSaveAndPublish={handlePublish}
        createdBy={agent?.created_by}
        isOwner={isOwner}
        canEdit={canEdit}
        isSharedWithMe={isSharedWithMe}
        isEmptyWidgetId={isEmptyWidgetId}
        isMyOrganization={isMyOrganization}
      />

      <div
        className={
          pathname.includes('/configuration') || pathname.includes('/integrations')
            ? 'min-h-0 flex-1 overflow-hidden'
            : 'min-h-0 flex-1 overflow-auto'
        }
      >
        {pathname.includes('/edit') ? (
          <div className="px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6">
            <EditAgent
              agentId={agentId}
              projectKey={blocksKey}
              agent={agent}
              isLoading={isLoadingAgent}
              isSaving={isSavingPreview}
              lastSaveTime={lastSaveTime}
              basePrompt={basePrompt}
              onBasePromptChange={setBasePrompt}
              onRegisterSave={handleRegisterEditSave}
            />
          </div>
        ) : pathname.includes('/configuration') ? (
          <div className="flex h-full min-h-0 flex-col overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
            <div
              data-testid="agent-config-workspace"
              className="flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden lg:flex-row lg:gap-5"
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <AgentConfigurationCard
                  agentId={agentId}
                  isAgentDetailsLoading={isLoadingAgent}
                  agent={agent}
                  createdBy={agent?.created_by}
                  sectionState={sectionState}
                  onSectionStateChange={handleSectionStateChange}
                  isLoadingLlmModels={isLoadingLlmModels}
                  availableChatModels={availableChatModels}
                  isLoadingCustomLlmModels={isLoadingCustomLlmModels}
                  availableCustomChatModels={availableCustomChatModels}
                  currentLlmModel={currentLlmModel}
                  currentLlmProvider={currentLlmProvider}
                  currentLlmModelId={currentLlmModelId}
                  isBlocksProvider={currentIsBlocksProvider}
                  onModelChange={({
                    modelName,
                    provider,
                    isBlocksProvider,
                    modelId,
                    temperature,
                    maxTokens,
                  }) => {
                    setSelectedLlmModel(modelName);
                    setSelectedLlmProvider(provider);
                    setSelectedLlmModelId(modelId || '');
                    setIsBlocksProvider(isBlocksProvider);
                    setTemperatureValue(temperature);
                    setMaxResponseValue(maxTokens);
                  }}
                  responseType={responseType}
                  onResponseTypeChange={(value) => {
                    setResponseType(value);
                    setResponseFormat(
                      value === 'json' ? responseFormat || defaultJsonResponseFormat : null
                    );
                  }}
                  responseFormat={responseFormat}
                  onResponseFormatChange={setResponseFormat}
                  basePrompt={basePrompt}
                  onBasePromptChange={setBasePrompt}
                  autoBasePrompt={autoBasePrompt}
                  temperatureValue={temperatureValue}
                  onTemperatureChange={setTemperatureValue}
                  maxResponseValue={maxResponseValue}
                  onMaxResponseChange={setMaxResponseValue}
                  isModelParametersOpen={isModelParametersOpen}
                  onModelParametersOpenChange={setIsModelParametersOpen}
                  enableQueryEnhancement={enableQueryEnhancement}
                  onEnableQueryEnhancementChange={setEnableQueryEnhancement}
                  scoreThreshold={scoreThreshold}
                  onScoreThresholdChange={setScoreThreshold}
                  topK={topK}
                  onTopKChange={setTopK}
                  denseWeight={denseWeight}
                  onDenseWeightChange={setDenseWeight}
                  projectKey={blocksKey}
                  selectedFolderIds={selectedFolderIds}
                  onSelectedFolderIdsChange={setSelectedFolderIds}
                  selectedToolIds={selectedToolIds}
                  onSelectedToolIdsChange={setSelectedToolIds}
                  selectedSkillIds={selectedSkillIds}
                  onSelectedSkillIdsChange={setSelectedSkillIds}
                  selectedSubagentIds={selectedSubagentIds}
                  onSelectedSubagentIdsChange={setSelectedSubagentIds}
                  enableSummary={enableSummary}
                  onEnableSummaryChange={setEnableSummary}
                  lastNConversations={lastNConversations}
                  onLastNConversationsChange={setLastNConversations}
                  agentName={agentName}
                  greeting={greeting}
                  onGreetingChange={setGreeting}
                  enableSmartSuggestions={enableSmartSuggestions}
                  onEnableSmartSuggestionsChange={setEnableSmartSuggestions}
                  enablePresetQuestions={enablePresetQuestions}
                  onEnablePresetQuestionsChange={setEnablePresetQuestions}
                  presetQuestions={presetQuestions}
                  onPresetQuestionsChange={setPresetQuestions}
                  guardrailOutputStrategy={guardrailOutputStrategy}
                  onGuardrailOutputStrategyChange={setGuardrailOutputStrategy}
                  configuredGuardrails={configuredGuardrails}
                  onConfiguredGuardrailsChange={setConfiguredGuardrails}
                  guardrailErrors={guardrailErrors}
                  enablePreValidation={enablePreValidation}
                  onEnablePreValidationChange={setEnablePreValidation}
                  enablePostValidation={enablePostValidation}
                  onEnablePostValidationChange={setEnablePostValidation}
                  enablePiiDetection={enablePiiDetection}
                  onEnablePiiDetectionChange={setEnablePiiDetection}
                  enableInjectionDetection={enableInjectionDetection}
                  onEnableInjectionDetectionChange={setEnableInjectionDetection}
                  inputRiskThreshold={inputRiskThreshold}
                  onInputRiskThresholdChange={setInputRiskThreshold}
                  outputRiskThreshold={outputRiskThreshold}
                  onOutputRiskThresholdChange={setOutputRiskThreshold}
                  enableKeywordCheck={enableKeywordCheck}
                  onEnableKeywordCheckChange={setEnableKeywordCheck}
                  bannedKeywords={bannedKeywords}
                  onBannedKeywordsChange={setBannedKeywords}
                  bannedPatterns={bannedPatterns}
                  onBannedPatternsChange={setBannedPatterns}
                  enableCustomRules={enableCustomRules}
                  onEnableCustomRulesChange={setEnableCustomRules}
                  customRules={customRules}
                  onCustomRulesChange={setCustomRules}
                />
              </div>
              <aside className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lg:h-auto lg:w-[340px] lg:flex-none xl:w-[360px]">
                <AgentsConfigPreviewSection
                  agent={agent}
                  projectKey={blocksKey}
                  isSaving={isSavingPreview}
                  lastSaveTime={lastSaveTime}
                />
              </aside>
            </div>
          </div>
        ) : pathname.includes('/integrations') ? (
          <div className="flex h-full min-h-0 flex-col overflow-hidden px-4 py-4 sm:px-6 sm:py-6">
            <AgentIntegrationHub
              id={agentId}
              allowedDomainsInput={allowedDomainsInput}
              onAllowedDomainsInputChange={setAllowedDomainsInput}
            />
          </div>
        ) : pathname.includes('/conversations') ? (
          <div className="px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6">
            <AgentConversation agentId={agentId} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-4 pt-4 sm:px-6 sm:pt-6">
            <div className="min-w-0 flex-1 space-y-6">
              {pathname.includes('/retrieval-testing') && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold tracking-tight">
                      {agentText(t, 'RETRIEVAL_TESTING', 'Retrieval Testing')}
                    </h3>
                  </div>
                  <AgentsRetrievalTesting agentId={agentId} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
